<template>
    <div class="fw-delegate-plan">
        <h3 v-if="groupName" class="fw-delegate-heading">{{ t.heading }} {{ groupName }}</h3>
        <p class="uk-text-meta">{{ t.intro }}</p>

        <div v-if="loading" class="uk-text-center uk-padding">
            <img src="/plugins/kosmoskosmos/festiware/assets/images/festiware_loading-spinner_v1.svg"
                 alt="Loading"
                 style="width:96px;height:96px"/>
        </div>

        <div v-else-if="!events.length" class="uk-alert uk-alert-primary">{{ t.empty }}</div>

        <template v-else>
            <!-- view controls: group by area vs. sort by time, plus the area filter (multiselect tags).
                 The area filter applies in both modes; shown only when more than one area exists. -->
            <div class="fw-plan-controls uk-margin">
                <div class="fw-controls-left">
                    <ul class="uk-subnav uk-subnav-pill fw-sort-toggle">
                        <li :class="{ 'uk-active': sortMode === 'area' }"><a href="#" @click.prevent="sortMode = 'area'">{{ t.byArea }}</a></li>
                        <li :class="{ 'uk-active': sortMode === 'time' }"><a href="#" @click.prevent="sortMode = 'time'">{{ t.byTime }}</a></li>
                    </ul>
                    <!-- downloads of the assigned plan; only meaningful once at least one shift is assigned -->
                    <div v-if="hasAssignments" class="fw-downloads">
                        <button type="button" class="uk-button uk-button-default uk-button-small" :disabled="exporting" @click="downloadExcel">{{ t.dlExcel }}</button>
                        <button type="button" class="uk-button uk-button-default uk-button-small" :disabled="exporting" @click="downloadIcs">{{ t.dlIcal }}</button>
                        <span v-if="exporting" uk-spinner="ratio: 0.6"></span>
                    </div>
                </div>
                <div v-if="areaOptions.length > 1" class="fw-area-filter">
                    <label class="uk-form-label fw-area-filter-label">{{ t.filterArea }}</label>
                    <Multiselect
                        v-model="selectedAreas"
                        mode="tags"
                        :options="areaOptions"
                        :searchable="true"
                        :close-on-select="false"
                        :can-clear="true"
                        :placeholder="t.filterAreaPlaceholder" />
                </div>
            </div>

            <div v-for="(group, area) in displayGroups" :key="area || '_default'" class="uk-margin">
                <h4 v-if="area" class="uk-margin-small-bottom">{{ area }}</h4>
                <table class="uk-table uk-table-divider uk-table-small fw-delegate-table">
                    <tbody>
                        <tr v-for="event in group" :key="event.id" :class="eventRowClass(event)">
                            <td class="fw-event-cell">
                                <div class="uk-text-bold">{{ event.name }}</div>
                                <div class="uk-text-meta">{{ event.date }}</div>
                                <div v-if="sortMode === 'time' && event.area" class="uk-text-meta">{{ event.area }}</div>
                            </td>
                            <td class="fw-jobs-cell">
                              <!-- jobs of one event laid out side by side (responsive grid) -->
                              <div class="fw-jobs-wrap">
                                <div v-for="job in event.jobs" :key="job.id" class="fw-job-row" :class="jobRowClass(job)">
                                    <div class="fw-job-name">
                                        {{ job.name }}
                                        <span v-if="job.break_time" class="uk-text-meta fw-job-meta">· {{ t.break }}: {{ job.break_time }}'</span>
                                    </div>

                                    <!-- occupied by someone outside the managed set: no name (GDPR) -->
                                    <span v-if="job.occupied" class="uk-label fw-occupied">{{ t.occupied }}</span>

                                    <!-- assignable: searchable single-select of self + delegates, candidates loaded on open.
                                         Single mode → one person per job slot, identical assign/unassign semantics as before.
                                         Locked (shift started/finished): disabled picker with a hint. -->
                                    <div v-else class="fw-job-control" :title="job.locked ? t.lockedHint : null">
                                        <Multiselect
                                                 class="fw-job-select"
                                                 :model-value="job.assigned_person ? job.assigned_person.id : null"
                                                 :options="optionsFor(job)"
                                                 :searchable="true"
                                                 :can-clear="true"
                                                 :loading="!!loadingCandidates[job.id]"
                                                 :disabled="isPending(job.id) || !!job.locked"
                                                 :placeholder="t.free"
                                                 :no-options-text="t.noCandidates"
                                                 :no-results-text="t.noCandidates"
                                                 @open="loadCandidates(job.id)"
                                                 @change="onSelect(job, $event || '')" />
                                        <span v-if="isPending(job.id)" uk-spinner="ratio: 0.5"></span>
                                    </div>
                                </div>
                              </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- per-person summary -->
            <div class="uk-margin-large-top">
                <h4>{{ t.summaryTitle }}</h4>
                <table class="uk-table uk-table-divider uk-table-small">
                    <thead>
                        <tr>
                            <th>{{ t.colPerson }}</th>
                            <th class="uk-text-right">{{ t.colShifts }}</th>
                            <th class="uk-text-right">{{ t.colHours }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="p in summary" :key="p.id" :class="{ 'fw-below-min': p.below_minimum }">
                            <td>{{ personLabel(p) }}</td>
                            <td class="uk-text-right">
                                {{ p.shift_count }}<template v-if="p.maximum"> / {{ p.maximum }}</template>
                                <span v-if="p.below_minimum" class="uk-text-warning uk-text-meta"> · {{ t.belowMin }} {{ p.minimum }}</span>
                            </td>
                            <td class="uk-text-right">{{ formatHours(p.minutes) }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </div>
</template>

<script>
import Multiselect from '@vueform/multiselect';
import '@vueform/multiselect/themes/default.css';

const TRANSLATIONS = {
    de: {
        heading: 'Einsatzplanung für',
        intro: 'Im Auswahlfeld erscheinen nur Mitwirkende, die für die jeweilige Schicht eingeteilt werden können (keine Überschneidung, eingehaltene Pausen- und Höchstarbeitszeit, Anwesenheit am Tag, passendes Team).',
        empty: 'Es stehen derzeit keine Schichten zur Einteilung bereit.',
        free: '— frei —',
        occupied: 'belegt',
        loading: 'lädt …',
        noCandidates: 'keine verfügbaren Mitwirkenden',
        filterArea: 'Nach Bereich filtern',
        filterAreaPlaceholder: 'Alle Bereiche',
        break: 'Pause',
        summaryTitle: 'Übersicht pro Person',
        colPerson: 'Person',
        colShifts: 'Schichten',
        colHours: 'Arbeitszeit',
        belowMin: 'min.',
        me: 'Ich',
        conflict: 'Die Einteilung ist nicht möglich (Konflikt mit einer anderen Schicht, Pause, Höchstarbeitszeit oder Anwesenheit).',
        taken: 'Diese Schicht wurde inzwischen anderweitig vergeben.',
        error: 'Die Aktion konnte nicht ausgeführt werden.',
        lockedHint: 'Diese Schicht wurde bereits begonnen oder abgeschlossen und kann nicht mehr geändert werden.',
        dlExcel: 'Excel',
        dlIcal: 'Kalender (iCal)',
        byArea: 'Nach Bereich',
        byTime: 'Nach Zeit',
    },
    en: {
        heading: 'Staff scheduling for',
        intro: 'The dropdown only lists members who can take the shift (no overlap, rest and maximum working time kept, present that day, matching team).',
        empty: 'No shifts are currently available for assignment.',
        free: '— free —',
        occupied: 'taken',
        loading: 'loading …',
        noCandidates: 'no available members',
        filterArea: 'Filter by area',
        filterAreaPlaceholder: 'All areas',
        break: 'Break',
        summaryTitle: 'Per-person overview',
        colPerson: 'Person',
        colShifts: 'Shifts',
        colHours: 'Working time',
        belowMin: 'min.',
        me: 'Me',
        conflict: 'Assignment not possible (conflict with another shift, rest period, maximum working time or attendance).',
        taken: 'This shift has meanwhile been assigned elsewhere.',
        error: 'The action could not be completed.',
        lockedHint: 'This shift has already started or finished and can no longer be changed.',
        dlExcel: 'Excel',
        dlIcal: 'Calendar (iCal)',
        byArea: 'By area',
        byTime: 'By time',
    },
};

export default {
    components: { Multiselect },
    props: ['lang', 'planData'],
    data() {
        return {
            loading: true,
            events: [],
            people: [],
            summary: [],
            groupName: '',          // comma-joined names of the managed group (heading)
            timetrackingActive: false,
            sortMode: 'area',        // 'area' = grouped by area | 'time' = flat, start-sorted, segmented by day
            exporting: false,        // a plan export (xlsx/ics) is in flight
            candidates: {},          // jobId -> [people]
            candidatesLoaded: {},    // jobId -> bool
            loadingCandidates: {},   // jobId -> bool
            pending: {},             // jobId -> bool (mutation in flight)
            selectedAreas: [],       // active area filter (empty = show all)
        };
    },
    computed: {
        t() {
            return TRANSLATIONS[this.lang] || TRANSLATIONS.en;
        },
        areaOptions() {
            const seen = [];
            this.events.forEach(e => {
                const area = e.area || '';
                if (area && !seen.includes(area)) seen.push(area);
            });
            return seen.map(a => ({ value: a, label: a }));
        },
        // grouping for display: by area (headings per area) or by time (start-sorted, segmented by start day).
        // the area filter (selectedAreas) applies in both modes.
        displayGroups() {
            const filter = this.selectedAreas;
            let evs = this.events;
            if (filter.length) evs = evs.filter(e => filter.includes(e.area || ''));
            if (this.sortMode === 'time') {
                evs = [...evs].sort((a, b) => String(a.start_at || '').localeCompare(String(b.start_at || '')));
            }
            const groups = {};
            evs.forEach(e => {
                // by time → segment by start day; by area → segment by area name
                const key = this.sortMode === 'time' ? this.dayLabel(e) : (e.area || '');
                (groups[key] = groups[key] || []).push(e);
            });
            return groups;
        },
        // at least one job filled by a managed person → the plan has something to export
        // (occupied-by-others jobs carry no name and are not part of the export)
        hasAssignments() {
            return this.events.some(e => (e.jobs || []).some(j => j.assigned_person));
        },
    },
    mounted() {
        // the assignment matrix is wide — widen the shared portal modal while it is open
        document.getElementById('module-modal-content')?.classList.add('fw-delegate-wide');
        if (this.planData) {
            this.events = this.planData.events || [];
            this.people = this.planData.people || [];
            this.summary = this.planData.summary || [];
            this.groupName = this.planData.group_name || '';
            this.timetrackingActive = !!this.planData.timetrackingActive;
            this.loading = false;
        } else {
            this.load();
        }
    },
    beforeUnmount() {
        document.getElementById('module-modal-content')?.classList.remove('fw-delegate-wide');
    },
    methods: {
        load() {
            this.loading = true;
            this.request('onGetDelegatePlan', {}).then((result) => {
                if (result) {
                    this.events = result.events || [];
                    this.people = result.people || [];
                    this.summary = result.summary || [];
                    this.groupName = result.group_name || '';
                    this.timetrackingActive = !!result.timetrackingActive;
                }
            }).finally(() => { this.loading = false; });
        },
        loadCandidates(jobId) {
            if (this.candidatesLoaded[jobId] || this.loadingCandidates[jobId]) return;
            this.loadingCandidates = { ...this.loadingCandidates, [jobId]: true };
            this.request('onGetJobCandidates', { job_id: jobId }).then((result) => {
                this.candidates = { ...this.candidates, [jobId]: (result && result.candidates) || [] };
                this.candidatesLoaded = { ...this.candidatesLoaded, [jobId]: true };
            }).finally(() => {
                this.loadingCandidates = { ...this.loadingCandidates, [jobId]: false };
            });
        },
        candidatesFor(job) {
            const list = this.candidates[job.id] || [];
            // never offer the already-assigned person twice (added separately in optionsFor)
            const assignedId = job.assigned_person ? job.assigned_person.id : null;
            return list.filter(c => c.id !== assignedId);
        },
        optionsFor(job) {
            // Multiselect options: keep the assigned person selectable/visible even before
            // candidates are loaded, then append the (already de-duped) candidate list.
            const options = [];
            if (job.assigned_person) {
                options.push({ value: job.assigned_person.id, label: this.personLabel(job.assigned_person) });
            }
            this.candidatesFor(job).forEach(c => {
                options.push({ value: c.id, label: this.personLabel(c) });
            });
            return options;
        },
        // per-job tint (used inside multi-job events where jobs differ in state)
        jobRowClass(job) {
            if (job.occupied) return 'fw-row-occupied';
            if (job.assigned_person) return 'fw-row-ours';
            return '';
        },
        // whole-row tint: only when the event's jobs share one state (the common single-job case),
        // so a mixed event isn't misleadingly painted — there the per-job tint carries the info.
        eventRowClass(event) {
            const jobs = event.jobs || [];
            if (!jobs.length) return '';
            if (jobs.every(j => j.occupied)) return 'fw-row-occupied';
            if (jobs.every(j => j.assigned_person && !j.occupied)) return 'fw-row-ours';
            return '';
        },
        onSelect(job, value) {
            if (this.isPending(job.id)) return;
            if (!value) {
                if (job.assigned_person) this.unassign(job);
                return;
            }
            this.assign(job, value);
        },
        assign(job, forPersonId) {
            this.setPending(job.id, true);
            this.request('onAssignJob', { job_id: job.id, for_person_id: forPersonId }, true).then((result) => {
                if (result && result.success) {
                    this.applyMutation(result);
                } else {
                    this.handleError(result);
                    this.load(); // resync on rejection (e.g. job taken meanwhile)
                }
            }).finally(() => { this.setPending(job.id, false); });
        },
        unassign(job) {
            this.setPending(job.id, true);
            this.request('onUnassignJob', { job_id: job.id }, true).then((result) => {
                if (result && result.success) {
                    this.applyMutation(result);
                } else {
                    this.handleError(result);
                    this.load();
                }
            }).finally(() => { this.setPending(job.id, false); });
        },
        applyMutation(result) {
            // patch the single job in place and refresh the summary; candidate lists
            // are re-computed on next focus, so other dropdowns need no invalidation.
            if (result.job) {
                outer: for (const event of this.events) {
                    for (let i = 0; i < event.jobs.length; i++) {
                        if (event.jobs[i].id === result.job.id) {
                            event.jobs.splice(i, 1, result.job);
                            break outer;
                        }
                    }
                }
                // the mutated job's candidate list is now stale → force reload on next focus
                this.candidatesLoaded = { ...this.candidatesLoaded, [result.job.id]: false };
                delete this.candidates[result.job.id];
            }
            if (result.summary) this.summary = result.summary;
        },
        handleError(result) {
            const code = result && result.error;
            let msg = this.t.error;
            if (code === 'conflict') msg = this.t.conflict;
            else if (code === 'job_taken') msg = this.t.taken;
            UIkit.notification({ message: '<span uk-icon="warning"></span> ' + msg, status: 'warning', pos: 'top-center', timeout: 5000 });
        },
        personLabel(p) {
            return p.is_self ? (p.name + ' (' + this.t.me + ')') : p.name;
        },
        formatHours(minutes) {
            if (!minutes) return '0h';
            const h = Math.floor(minutes / 60);
            const m = minutes % 60;
            return m ? `${h}:${String(m).padStart(2, '0')}h` : `${h}h`;
        },
        dayLabel(e) {
            if (!e.start_at) return '—';
            const d = new Date(e.start_at);
            return d.toLocaleDateString(this.lang === 'de' ? 'de-DE' : 'en-GB', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
        },
        triggerDownload(filename, content, mime) {
            const blob = new Blob([content], { type: mime });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = filename;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a); URL.revokeObjectURL(url);
        },
        downloadExcel() { this.exportPlan('xlsx'); },
        downloadIcs() { this.exportPlan('ics'); },
        // server builds the file (xlsx via Maatwebsite, ics text); returned inline
        // (base64 for xlsx, plain text for ics) and handed to the browser as a download.
        exportPlan(format) {
            if (this.exporting) return;
            this.exporting = true;
            this.request('onPlanExport', { format, lang: this.lang }).then((res) => {
                if (!res) {
                    UIkit.notification({ message: '<span uk-icon="warning"></span> ' + this.t.error, status: 'warning', pos: 'top-center', timeout: 5000 });
                    return;
                }
                if (format === 'ics') {
                    this.triggerDownload(res.filename || 'einsatzplan.ics', res.content || '', res.mime || 'text/calendar;charset=utf-8');
                } else {
                    const bin = atob(res.base64 || '');
                    const bytes = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
                    this.triggerDownload(res.filename || 'einsatzplan.xlsx', bytes, res.mime || 'application/octet-stream');
                }
            }).finally(() => { this.exporting = false; });
        },
        isPending(jobId) {
            return !!this.pending[jobId];
        },
        setPending(jobId, value) {
            this.pending = { ...this.pending, [jobId]: value };
        },
        request(name, data, silent = false) {
            return new Promise((resolve) => {
                oc.ajax(name, {
                    data: { data: JSON.stringify(data) },
                    progressBar: !silent,
                    flash: !silent,
                    success: (d) => resolve(d),
                    error: (jqXHR) => resolve(jqXHR && jqXHR.responseJSON ? jqXHR.responseJSON : null),
                });
            });
        },
    },
};
</script>

<style>
/* the assignment matrix uses the full screen width (toggled on #module-modal-content while open).
   Must out-specify the portal's `#module-modal .uk-modal-dialog { max-width: 900px !important }`. */
#module-modal .uk-modal-dialog.fw-delegate-wide { width: 96vw !important; max-width: 96vw !important; }
.fw-delegate-heading { margin-bottom: 4px; }
/* normal vue-form sizing for both multiselects (area filter + job pickers) */
.fw-delegate-plan .multiselect {
    --ms-font-size: 0.875rem;
    --ms-line-height: 1.35;
    --ms-py: 0.25rem;
    --ms-px: 0.5rem;
    --ms-radius: 2px;
    --ms-tag-font-size: 0.8rem;
    min-height: 36px;
}
.fw-delegate-table td { vertical-align: top; }
.fw-event-cell { width: 24%; }
/* jobs of one event side by side: responsive grid, fills the cell width */
.fw-jobs-wrap { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 8px 16px; }
.fw-job-row { display: flex; flex-direction: column; gap: 2px; min-width: 0; padding: 4px 8px; border-radius: 4px; }
.fw-job-name { font-weight: 500; }
.fw-job-control { display: flex; align-items: center; gap: 8px; min-width: 0; }
.fw-job-select { width: 100%; min-width: 0; }
/* view controls bar: sort toggle + downloads left, area filter right */
.fw-plan-controls { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; flex-wrap: wrap; }
.fw-controls-left { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.fw-sort-toggle { margin: 0; }
.fw-area-filter { max-width: 360px; min-width: 220px; flex: 1 1 220px; }
.fw-area-filter-label { display: block; margin-bottom: 4px; }
.fw-occupied { background: #999; }
.fw-downloads { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; }
.fw-below-min td { background: rgba(255, 196, 0, 0.08); }
/* row tints: grey = occupied by someone else, light green = filled by one of ours.
   Whole row → tint the <td> cells (the tr background sits behind them);
   per-job <div> → tint directly (for mixed multi-job events). */
tr.fw-row-occupied td, .fw-job-row.fw-row-occupied { background: #f2f2f2; }
tr.fw-row-ours td, .fw-job-row.fw-row-ours { background: #e6f6e6; }
@media (prefers-color-scheme: dark) {
    tr.fw-row-occupied td, .fw-job-row.fw-row-occupied { background: rgba(255, 255, 255, 0.06); }
    tr.fw-row-ours td, .fw-job-row.fw-row-ours { background: rgba(40, 167, 69, 0.20); }
}
[data-theme="dark"] tr.fw-row-occupied td, [data-theme="dark"] .fw-job-row.fw-row-occupied { background: rgba(255, 255, 255, 0.06); }
[data-theme="dark"] tr.fw-row-ours td, [data-theme="dark"] .fw-job-row.fw-row-ours { background: rgba(40, 167, 69, 0.20); }
@media (max-width: 640px) {
    .fw-event-cell, .fw-jobs-cell { display: block; width: 100%; }
    .fw-jobs-wrap { grid-template-columns: 1fr; }
}
</style>
